import { computed, reactive, ref, watch } from "vue";
import { useNgSession } from "@sazka/ng/ng-session";
import { sazkaAlert } from "@sazka/utils/sazka-alert";
import { fetchPlayerOnboarding } from "../api/bonusomat-player-data-api";
import { fetchLogoutOnboarding } from "../api/bonusomat-logout-data-api";
import { BonusomatOnboarding } from "../models/bonusomat-onboarding";
import { BonusomatOnboardingModal } from "../models/bonusomat-onboarding-modal";
import { logException } from "@sazka/utils/logger/use-app-logger";
import { useLocalStorage } from "@sazka/utils/use-storage";

const { session } = useNgSession();
const playerId = computed(() => session.value?.PlayerID);

const onboardingActiveStep = ref(0);
const onboardingActiveStepChangedWatch = ref(false);
const onboardingData = ref<BonusomatOnboarding>(null);
const onboardingModal = reactive<BonusomatOnboardingModal>({
    isOpened: false,
    page: null,
});

async function loadOnboardingData() {
    if (playerId.value) {
        onboardingData.value = await fetchPlayerOnboarding(playerId.value);
    } else {
        onboardingData.value = await fetchLogoutOnboarding();
    }
}

async function onboardingOpenStep(step: number) {
    onboardingActiveStep.value = step;
    onboardingActiveStepChangedWatch.value = !onboardingActiveStepChangedWatch.value;
}

async function openBonusomatOnboarding(page?: number | string, disableError?: boolean) {
    try {
        if (onboardingData.value) return;

        await loadOnboardingData();
    } catch (error) {
        if (disableError) return;
        logException("bonusomat-onboarding", error, { message: "Open bonusomat onboarding error" });
        sazkaAlert("Omlouváme se, při načítání informací nastala chyba.", "Zkuste to prosím později.");
    } finally {
        if (page !== null && page !== undefined) {
            onboardingModal.page = page;
        }

        if (onboardingData.value) {
            onboardingModal.isOpened = true;
        }
    }
}

const storageKey = "bonusomat-onboarding-visited";
const storageValue = "true";

function wasOnboardingAlreadyVisited() {
    return useLocalStorage().getItem(storageKey) === storageValue;
}

async function setOnboardingAlreadyVisited() {
    useLocalStorage().setItem(storageKey, storageValue, "essential");
}

// Reload data for the case that player has different board segment than the one for not-logged players
watch(playerId, async () => {
    if (onboardingData.value) {
        await loadOnboardingData();
    }
});

export default function useBonusomatOnboarding() {
    return {
        onboardingActiveStep,
        onboardingActiveStepChangedWatch,
        onboardingData,
        onboardingModal,

        onboardingOpenStep,
        openBonusomatOnboarding,
        wasOnboardingAlreadyVisited,
        setOnboardingAlreadyVisited,
    };
}
